import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listTree, padKey, recordsContext } from '../../extensions/bundled/documents/documentsStore';
import {
  indexNodes,
  isDescendant,
  childrenOf,
  collectSubtree as collectSubtreeOf,
} from '../../extensions/bundled/documents/documentsTree';
import {
  useDocumentsUiStore,
  subscribeDocumentChanges,
  clearDocumentReveal,
} from '../../extensions/bundled/documents/documentsUiStore';
import { KEY_STEP, cmpNodes } from './treeModel';
import useDocSpaces from './useDocSpaces';
import { filterDocsByProject } from './documentsProjectFilter';

/**
 * One section's tree data: the loaded nodes, the collapse state of the section
 * and of each folder (both persisted to localStorage per section id), the
 * reload triggers (treeVersion nonce, WS fast path, first search) and the
 * derived index the rows and actions read from.
 *
 * Lazy: a team section's tree is only fetched the first time it's expanded
 * (or on mount, if it was already expanded last session); Personal (`eager`)
 * always loads immediately.
 */
export default function useSectionTree({ sectionId, scope, eager, query, treeVersion }) {
  const collapseKey = `documents-section:${sectionId}`;
  const foldersKey = `documents-section-folders:${sectionId}`;

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(collapseKey) === '1';
    } catch {
      return false;
    }
  });
  const [nodes, setNodes] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(() => {
    try {
      const raw = localStorage.getItem(foldersKey);
      const ids = raw ? JSON.parse(raw) : null;
      return Array.isArray(ids) ? new Set(ids.map(String)) : new Set();
    } catch {
      return new Set();
    }
  });
  const hasLoadedRef = useRef(false);
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;

  const load = useCallback(async () => {
    hasLoadedRef.current = true;
    try {
      setNodes(await listTree(scope));
    } catch {
      setNodes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.type, scope.teamId]);

  // Expand every id in `ids` at once (folders AND documents-with-subpages —
  // the persisted set is id-keyed and shared by both, see TreeRow), in one
  // state update and one localStorage write. `expandFolder` below is the
  // single-id case of this.
  const expandFolders = useCallback(
    (ids) => {
      if (!ids || !ids.length) return;
      setExpandedFolders((prev) => {
        if (ids.every((id) => prev.has(id))) return prev;
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        try {
          localStorage.setItem(foldersKey, JSON.stringify([...next]));
        } catch {
          /* ignore quota errors */
        }
        return next;
      });
    },
    [foldersKey]
  );

  /**
   * Consume a pending "an external writer created this node" reveal — see
   * `documentsUiStore.pendingReveal` and `mcp/reveal.js`.
   *
   * Called only AFTER a reload has finished, because the node the request
   * names does not exist in this section's tree at the moment the request is
   * made; the bump that triggers the reload is fired immediately after it.
   *
   * Three properties this has to hold, and how:
   *
   * - **Scope-correct.** The request carries the MCP wire scope, which is
   *   byte-identical to `sectionId` (`personal` / `team:<id>`); a section whose
   *   id does not match returns without reading further and WITHOUT clearing,
   *   so the section that owns the node still gets it.
   * - **Exactly once.** Cleared by nonce the moment it is applied, so a later
   *   re-render, a second reload, or the WS fast path cannot re-expand a
   *   folder the user has collapsed by hand in the meantime.
   * - **In place only.** Expands ancestors. It does not select, rename, open a
   *   tab, or expand the section itself — a collapsed SECTION is a deliberate,
   *   persisted, per-workspace choice with its own visible affordance, and
   *   reopening one on behalf of a background process is a bigger move than
   *   the owner asked for. (The in-app "+" path does un-collapse, because a
   *   human just clicked "+" inside that very section.)
   */
  const consumeReveal = useCallback(() => {
    // `getState?.` — a few component tests stub the store as a bare hook.
    const pending = useDocumentsUiStore.getState?.()?.pendingReveal;
    if (!pending || pending.scope !== sectionId) return;
    expandFolders(pending.ancestorIds);
    clearDocumentReveal(pending.nonce);
  }, [expandFolders, sectionId]);

  // Personal loads immediately; a team section loads on mount only if it was
  // already expanded last session, otherwise waits for the first expand.
  // A reveal parked for this section before it mounted (a breadcrumb or link
  // that switched the sidebar to this tree, card 5.3) is consumed after the
  // first load too.
  useEffect(() => {
    let cancelled = false;
    if (eager || !collapsed) {
      (async () => {
        await load();
        if (!cancelled) consumeReveal();
      })();
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eager]);

  // A search that reaches into this section before it's ever been expanded
  // (e.g. typing straight into the filter with every team section collapsed)
  // should still search its data instead of silently reporting "No matches."
  // — load() is a no-op past the very first call (hasLoadedRef flips
  // synchronously), so this is cheap on every keystroke.
  useEffect(() => {
    if (query && !hasLoadedRef.current) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Reload on the global "something changed" nonce — same coarse trigger every
  // section reacted to before sections existed — but only once this section
  // has data to begin with (skip the mount-time value) and only a loaded
  // section (an unloaded, collapsed section reloads lazily on its own on
  // first expand instead).
  const skipFirstVersionRef = useRef(true);
  useEffect(() => {
    if (skipFirstVersionRef.current) {
      skipFirstVersionRef.current = false;
      return undefined;
    }
    if (!hasLoadedRef.current) return undefined;
    let cancelled = false;
    // The reveal is consumed only once the reload has LANDED: the node an
    // external writer just created is not in `nodes` yet when the nonce moves.
    (async () => {
      await load();
      if (!cancelled) consumeReveal();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeVersion]);

  // WS-first fast path: a live tree-affecting record (folder/document, not a
  // docbody) for this exact scope reloads this section immediately, without
  // waiting for the engine write + disk re-read that drives treeVersion above.
  // Only while the section is actually visible — a collapsed section catches
  // up via the treeVersion bump (or the next time it's expanded).
  useEffect(() => {
    const myScopeKey = recordsContext(scope).workspaceId;
    return subscribeDocumentChanges((scopeKey, records) => {
      if (scopeKey !== myScopeKey) return;
      if (collapsedRef.current || !hasLoadedRef.current) return;
      const treeAffecting = records.some((r) => r.record_type === 'folder' || r.record_type === 'document');
      if (treeAffecting) load();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.type, scope.teamId]);

  const toggleSection = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(collapseKey, next ? '1' : '0');
      } catch {
        /* ignore quota errors */
      }
      if (!next && !hasLoadedRef.current) load();
      return next;
    });
  }, [collapseKey, load]);

  // ONE index, built by the shared tree module, so the sidebar's notion of
  // "the subtree of this node" and MCP's are the same code rather than two
  // walks that can drift apart. `cmpNodes` is passed through because the
  // render path depends on each sibling group being ordered.
  //
  // Project scope (card 4.1): what RENDERS is the project's part of the tree
  // (`treeIndex`); deletes, moves and new sort keys use the FULL tree
  // (`fullIndex`), so a child hidden by the project filter is never left
  // behind by a delete or skipped by a move.
  // …and, under a project, only the selected documentation space (card 4.3).
  const projectScope = useDocSpaces().filterScope;
  const fullIndex = useMemo(() => indexNodes(nodes, cmpNodes), [nodes]);
  const shownNodes = useMemo(() => filterDocsByProject(nodes, projectScope), [nodes, projectScope]);
  const treeIndex = useMemo(() => (shownNodes === nodes ? fullIndex : indexNodes(shownNodes, cmpNodes)), [shownNodes, nodes, fullIndex]);
  const nodeById = treeIndex.nodeById;
  const childrenByParent = treeIndex.childrenByParent;
  // Top-level nodes live under a Symbol bucket, not the string 'root' — see
  // documentsTree.js: a node whose id was literally "root" used to share the
  // top-level bucket, and a delete of it would have taken the whole tree.
  const rootLevel = useCallback((excludeId) => childrenOf(treeIndex, null).filter((n) => n.id !== excludeId), [treeIndex]);

  const orderedChildren = useCallback(
    (parentId, excludeId) =>
      parentId == null
        ? rootLevel(excludeId)
        : (childrenByParent.get(parentId) || []).filter((n) => n.id !== excludeId),
    [childrenByParent, rootLevel]
  );

  const searching = query.length > 0;

  const visibleIds = useMemo(() => {
    if (!searching) return null;
    const visible = new Set();
    const dfs = (id) => {
      const node = nodeById.get(id);
      let anyChild = false;
      for (const child of childrenByParent.get(id) || []) {
        if (dfs(child.id)) anyChild = true;
      }
      // "#tag" (a tag chip's filter, card 2.3) matches a document's tags.
      const selfMatch =
        query.startsWith('#') && query.length > 1
          ? (node?.tags || []).some((tag) => tag.toLowerCase() === query.slice(1))
          : (node?.name || '').toLowerCase().includes(query);
      if (selfMatch || anyChild) visible.add(id);
      return selfMatch || anyChild;
    };
    for (const root of childrenOf(treeIndex, null)) dfs(root.id);
    return visible;
  }, [searching, query, nodeById, childrenByParent, treeIndex]);

  // The last key among ALL siblings (hidden ones too), so a new node never
  // sorts before a sibling the project filter hides.
  const endKey = useCallback(
    (parentId, excludeId) => {
      const siblings = childrenOf(fullIndex, parentId ?? null).filter((n) => n.id !== excludeId);
      const last = siblings[siblings.length - 1];
      return padKey((last ? Number(last.sortKey) : 0) + KEY_STEP);
    },
    [fullIndex]
  );

  // Both of these are the shared implementations now (documentsTree.js): the
  // cycle guard MCP needs is the same guard the sidebar wants, and "the subtree
  // of this node" must mean one thing in this app, not two. On the FULL tree.
  const isAncestor = useCallback(
    (ancestorId, nodeId) => isDescendant(fullIndex, nodeId, ancestorId),
    [fullIndex]
  );

  const collectSubtree = useCallback((rootId) => collectSubtreeOf(fullIndex, rootId), [fullIndex]);

  const toggleFolder = useCallback(
    (id) => {
      setExpandedFolders((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        try {
          localStorage.setItem(foldersKey, JSON.stringify([...next]));
        } catch {
          /* ignore quota errors */
        }
        return next;
      });
    },
    [foldersKey]
  );

  const expandFolder = useCallback((id) => expandFolders([id]), [expandFolders]);

  // Creating at the section root (parentId null, via the section header's own
  // "+" buttons or a titlebar event) must reveal the new item — expand a
  // collapsed section rather than leaving the create invisible behind it.
  const ensureSectionExpanded = useCallback(() => {
    setCollapsed((prev) => {
      if (!prev) return prev;
      try {
        localStorage.setItem(collapseKey, '0');
      } catch {
        /* ignore quota errors */
      }
      return false;
    });
  }, [collapseKey]);

  return {
    collapsed,
    nodes, // the FULL tree (exports, duplicate, subtree work)
    shownNodes, // what the project scope shows (counts, pins)
    expandedFolders,
    load,
    toggleSection,
    treeIndex,
    nodeById,
    childrenByParent,
    rootChildren: childrenOf(treeIndex, null),
    orderedChildren,
    searching,
    visibleIds,
    endKey,
    isAncestor,
    collectSubtree,
    toggleFolder,
    expandFolder,
    ensureSectionExpanded,
  };
}
